import {
    collection,
    doc,
    getDocs,
    getDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    onSnapshot,
    serverTimestamp,
    type Firestore,
    type Unsubscribe
} from 'firebase/firestore';
import { randomUUID } from '@/lib/utils/utils';
import { BUILTIN_MARK_STYLES } from './manuscript-constants';

/**
 * Service for managing the user's manuscript collection records.
 * Path: users/{userId}/manuscripts/{manuscriptId}
 */
export const ManuscriptCollectionService = {
    /**
     * Fetch all manuscripts for a user.
     */
    async fetchManuscripts(db: Firestore, userId: string): Promise<Manuscript[]> {
        if (!userId) return [];
        const colRef = collection(db, 'users', userId, 'manuscripts');
        const snapshot = await getDocs(colRef);
        return snapshot.docs.map(doc => doc.data() as Manuscript);
    },

    /**
     * Subscribe to real-time updates for the collection.
     */
    subscribeToManuscripts(
        db: Firestore,
        userId: string,
        onUpdate: (manuscripts: Manuscript[]) => void
    ): Unsubscribe {
        if (!userId) return () => { };
        const colRef = collection(db, 'users', userId, 'manuscripts');
        return onSnapshot(colRef, (snapshot) => {
            onUpdate(snapshot.docs.map(doc => doc.data() as Manuscript));
        });
    },

    /**
     * Create a new manuscript record.
     */
    async createManuscript(
        db: Firestore,
        userId: string,
        data: Partial<Manuscript>
    ): Promise<string> {
        if (!userId) throw new Error('User ID required');
        const id = data.id || randomUUID();
        const docRef = doc(db, 'users', userId, 'manuscripts', id);

        const now = Date.now();
        const manuscript: Manuscript = {
            id,
            name: data.name || 'Untitled Manuscript',
            version: data.version || 'CSB',
            scope: data.scope || 'global',
            color: data.color || '#6366f1', // Indigo default
            sortOrder: data.sortOrder || 0,
            isDefault: data.isDefault || false,
            markStyles: (data.markStyles && data.markStyles.length > 0) ? data.markStyles : BUILTIN_MARK_STYLES,
            dateCreated: now,
            dateUpdated: now
        };

        await setDoc(docRef, {
            ...manuscript,
            serverTimestamp: serverTimestamp()
        });

        return id;
    },

    /**
     * Update manuscript metadata.
     */
    async updateManuscript(
        db: Firestore,
        userId: string,
        id: string,
        updates: Partial<Manuscript>
    ): Promise<void> {
        if (!userId || !id) return;
        const docRef = doc(db, 'users', userId, 'manuscripts', id);
        await updateDoc(docRef, {
            ...updates,
            dateUpdated: Date.now(),
            serverTimestamp: serverTimestamp()
        });
    },

    /**
     * Delete a manuscript record.
     * Note: This does not automatically delete the subcollection of passages.
     * In a production environment, this should be handled by a Cloud Function
     * or recursive client-side deletion (if data is small enough).
     */
    async deleteManuscript(db: Firestore, userId: string, id: string): Promise<void> {
        if (!userId || !id) return;
        const docRef = doc(db, 'users', userId, 'manuscripts', id);
        await deleteDoc(docRef);
        // TODO: Consider deleting the 'passages' subcollection if needed
    },

    /**
     * Ensures at least one default manuscript exists.
     */
    async ensureDefaultManuscript(db: Firestore, userId: string, version: string = 'CSB', preferredId?: string): Promise<Manuscript> {
        const manuscripts = await this.fetchManuscripts(db, userId);
        const existingDefault = manuscripts.find(m => m.isDefault && m.version === version);

        if (existingDefault) return existingDefault;

        // Check if there is any manuscript for this version, use it as default if so
        const versionMatch = manuscripts.find(m => m.version === version);
        if (versionMatch) {
            await this.updateManuscript(db, userId, versionMatch.id, { isDefault: true });
            return { ...versionMatch, isDefault: true };
        }

        const id = preferredId || randomUUID();
        await this.createManuscript(db, userId, {
            id,
            name: 'Personal Study',
            version,
            scope: 'global',
            color: '#6366f1', // Indigo
            isDefault: true,
            sortOrder: -100, // Always top
            markStyles: BUILTIN_MARK_STYLES
        });

        // Re-fetch to get complete object
        const docRef = doc(db, 'users', userId, 'manuscripts', id);
        const snap = await getDoc(docRef);
        return snap.data() as Manuscript;
    },

    /**
     * Filtering logic to determine which manuscripts are visible for a given context.
     */
    getManuscriptsForPassage(
        manuscripts: Manuscript[],
        passageId: string,
        bookName: string,
        version: string
    ): Manuscript[] {
        return manuscripts.filter(m => {
            if (m.version !== version) return false;

            switch (m.scope) {
                case 'global':
                    return true;
                case 'book':
                    return m.bookName === bookName;
                case 'passages':
                    return m.passageIds?.includes(passageId) ?? false;
                default:
                    return false;
            }
        }).sort((a, b) => a.sortOrder - b.sortOrder);
    }
};
